import moment from "moment-timezone";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardHeader } from "reactstrap";
import { mutate as unboundMutate } from "swr";
import { TableContext } from "../../../components/Table/context";
import { useAccess } from "../../../utils/hooks/access";
import { useElementHeight } from "../../../utils/hooks/useElementHeight";
import { useVisible } from "../../../utils/hooks/useVisible";
import { useColumnsPreferences } from "../../Settings/Configuration/hooks/useColumnsPreferences";
import { usePreferences } from "../../Settings/Configuration/hooks/usePreferences";
import { useColumnsVisibilityPreferences } from "../../Settings/Configuration/hooks/useColumnsVisibilityPreferences";
import { useGroups } from "../../Settings/General/OrganisationLevelGroups/context";
import Summary from "../Home/Actions/Summary";
import { useWeeklyTeam } from "../api/useWeeklyTeam";
import CancelModal from "../components/TimeSheetTable/CancelModal";
import { useOverview } from "../components/Topbar/context";
import { useFilters } from "../filtersContext";
import { useBindPayGroupWithTable } from "../hooks/useBindPayGroupWithTable";
import { useDateFilters } from "../hooks/useDateFilters";
import Actions from "./Actions";
import { useCopyTimeSheet } from "./Actions/useCopyTimeSheet";
import ProfileModal from "./ProfileDrawer";
import Table from "./Table";
import { useTeamTable } from "./Table/hooks/useTable";
import { useColumns } from "./Table/useColumns";
import { useBulkActions } from "./hooks/useBulkActions";
import { getUserTimeSheetsSWRKey } from "./hooks/useFetchUserTimeSheets";
import { useSummary } from "./hooks/useSummary";
import History from "../Home/Actions/HistoryDrawer";

const Content = () => {
  const hideUserProfile = usePreferences(
    "timeSheetWeekly.hideUserProfile"
  );
  const { hasAccess: canViewRates } = useAccess("canViewRates");
  const { groups } = useGroups();
  const groupWithBudget = useMemo(
    () => groups?.find(({ hasBudget }) => hasBudget),
    [groups]
  );

  const { ref: actionsRef, height: actionsHeight } = useElementHeight();

  const { from, to, mode, payGroup } = useDateFilters();
  const [filters, changeFilters] = useFilters();
  const [state, setState] = useState();
  const [expandedUsers, setExpandedUsers] = useState([]);
  const tableFilters = useMemo(
    () => ({
      filters,
      ...state,
    }),
    [filters, state]
  );

  const refetchExpandedUsers = useCallback(() => {
    return Promise.all(
      expandedUsers.map((user) => {
        return unboundMutate(getUserTimeSheetsSWRKey(user));
      })
    );
  }, [expandedUsers]);

  const { fetch: baseFetchOverview } = useOverview();

  const {
    data: { result: data, totalItems },
    setData,
    isLoading: loadingUsers,
    mutate,
  } = useWeeklyTeam(
    state && {
      from,
      to,
      filters,
      ...state,
    }
  );

  const {
    visible: isProfileVisible,
    open: openProfile,
    selected: selectedUser,
    close: closeProfile,
  } = useVisible();

  const {
    visible: isHistoryOpen,
    open: openHistory,
    selected: selectedUserHistory,
    close: closeHistory,
  } = useVisible();

  const {
    visible: isCancelModalVisible,
    open: openCancelModal,
    selected: cancelCallback,
    close: closeCancelModal,
  } = useVisible();

  const fetchOverview = useCallback(
    (config) => {
      baseFetchOverview({
        url: "/time-sheet/weekly/team/overview",
        ...config,
        params: {
          ...config.params,
          budgetBy:
            groupWithBudget && `${groupWithBudget.level}`.replace("_", ""),
        },
      });
    },
    [baseFetchOverview, groupWithBudget]
  );

  const refetchOverview = useCallback(() => {
    fetchOverview({
      params: { from, to, filters: filters.filters },
    });
  }, [fetchOverview, from, to, filters]);

  const refetchUsers = useCallback(() => {
    return mutate();
  }, [mutate]);

  const { approve, unapprove, lock, unlock } = useBulkActions({
    refetch: useCallback(() => {
      return Promise.all([
        refetchOverview(),
        refetchUsers(),
        refetchExpandedUsers(),
      ]);
    }, [refetchOverview, refetchUsers, refetchExpandedUsers]),
    filters: tableFilters,
  });

  const {
    visible: summaryVisible,
    selected: summaryData,
    openDaySummary,
    openAllSummary,
    close: closeSummary,
  } = useSummary();

  const [openCopyModal, copyModal] = useCopyTimeSheet({
    filters: useMemo(() => {
      return {
        startDate: moment(from).format("YYYY-MM-DD"),
        endDate: moment(to).format("YYYY-MM-DD"),
      };
    }, [from, to]),
    refetch: useCallback(() => {
      return Promise.all([
        refetchOverview(),
        refetchUsers(),
        refetchExpandedUsers(),
      ]);
    }, [refetchOverview, refetchUsers, refetchExpandedUsers]),
    canViewRates,
  });

  const columns = useColumns({
    viewSummary: openAllSummary,
    viewProfile: !hideUserProfile && openProfile,
    canViewRates,
  });

  const table = useTeamTable({
    columns: useColumnsPreferences(columns, "timeSheetWeekly"),
    data,
    setData,
    totalItems,
    refetchOverview,
    refetchUsers,
    openCancelModal,
  });

  useEffect(() => {
    setExpandedUsers(Object.keys(table.state.expanded));
  }, [table.state.expanded, setExpandedUsers]);

  const hiddenColumnsMap = useColumnsVisibilityPreferences("timeSheetWeekly");
  useEffect(() => {
    if (hiddenColumnsMap) {
      table.setHiddenColumns(Object.keys(hiddenColumnsMap));
    }
  }, [table, hiddenColumnsMap]);

  const { pageIndex, pageSize, sortBy } = table.state;

  useEffect(() => {
    setState({ pageIndex, pageSize, sortBy });
  }, [pageIndex, pageSize, sortBy]);

  useEffect(() => {
    const controller = new AbortController();
    fetchOverview({
      params: {
        from,
        to,
        filters,
      },
      signal: controller.signal,
    });
    return () => controller.abort();
  }, [fetchOverview, from, to, filters]);

  useBindPayGroupWithTable({
    filters,
    setFilters: changeFilters,
    mode,
    payGroup,
  });

  return (
    <TableContext.Provider value={table}>
      <Card innerRef={actionsRef} className="mb-0 position-static">
        <CardHeader className="border-top p-3">
          <Actions
            approve={approve}
            unapprove={unapprove}
            lock={lock}
            unlock={unlock}
            copy={openCopyModal}
          />
        </CardHeader>
      </Card>

      <div style={{ maxHeight: `calc(100% - ${actionsHeight}px)` }}>
        <Table
          loading={loadingUsers}
          openDaySummary={openDaySummary}
          openCopyModal={openCopyModal}
          openHistory={openHistory}
        />
      </div>

      {isProfileVisible && (
        <ProfileModal
          user={selectedUser}
          visible={isProfileVisible}
          close={closeProfile}
        />
      )}

      {summaryVisible && (
        <Summary
          open={summaryVisible}
          close={closeSummary}
          user={summaryData?.user}
          range={summaryData?.range}
          canViewRates={canViewRates}
        />
      )}

      {isHistoryOpen && 
        <History 
          open={isHistoryOpen} 
          close={closeHistory}
          user={selectedUserHistory.user}
          selected={selectedUserHistory}
        />      
      }

      {copyModal}

      <CancelModal
        isOpen={isCancelModalVisible}
        close={closeCancelModal}
        reset={cancelCallback}
      />
    </TableContext.Provider>
  );
};

export default Content;
